// Экран «Новый чат» / директория (SPEC §4.1, FR — выбор коллеги в директории). Источник списка —
// transport.rest.listUsers (директория, НЕ-стор-данные §4.0). Тап по коллеге → createDirect; режим
// группы → мультивыбор + название → createGroup. Созданный чат вносится в стор через actions.ingestChat
// (сервер не шлёт chat.created по WS) и сразу открывается. Доменное чтение/запись чатов — через стор.

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useNavigate } from 'react-router-dom'
import type { PublicUser, UserId } from '@tele-chat/shared-contracts'
import { Avatar, Button, IconButton, Spinner } from '@tele-chat/design-system'
import { useRest } from '../transport/TransportContext.js'
import { useStore } from '../store/StoreContext.js'
import { useSelf } from '../auth/SessionContext.js'
import { EmptyState } from '../components/EmptyState.js'
import { chatPath } from '../routing/deep-link.js'
import { strings } from '../i18n/strings.js'

type Mode = 'direct' | 'group'

export function NewChatScreen(): JSX.Element {
  const rest = useRest()
  const store = useStore()
  const self = useSelf()
  const navigate = useNavigate()

  const [mode, setMode] = useState<Mode>('direct')
  const [query, setQuery] = useState('')
  const [users, setUsers] = useState<PublicUser[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Set<UserId>>(new Set())
  const [groupTitle, setGroupTitle] = useState('')
  const [creating, setCreating] = useState(false)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)

  const load = useCallback(
    async (q: string): Promise<void> => {
      setLoading(true)
      try {
        const list = await rest.listUsers(q.trim().length > 0 ? q.trim() : undefined)
        // себя в директории не показываем (личный чат с собой не создаём).
        setUsers(self !== null ? list.filter((u) => u.id !== self.id) : list)
      } catch {
        setUsers([])
      } finally {
        setLoading(false)
      }
    },
    [rest, self],
  )

  useEffect(() => {
    if (debounce.current !== null) clearTimeout(debounce.current)
    debounce.current = setTimeout(() => void load(query), 250)
    return () => {
      if (debounce.current !== null) clearTimeout(debounce.current)
    }
  }, [query, load])

  // Открыть (или создать) личный чат с коллегой.
  const openDirect = useCallback(
    async (userId: UserId): Promise<void> => {
      if (creating) return
      setCreating(true)
      try {
        const chat = await rest.createDirect(userId)
        store.actions.ingestChat(chat)
        navigate(chatPath(chat.id), { replace: true })
      } catch {
        setCreating(false)
      }
    },
    [rest, store, navigate, creating],
  )

  const toggleSelect = useCallback((userId: UserId): void => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }, [])

  const createGroup = useCallback(async (): Promise<void> => {
    if (creating || selected.size === 0 || groupTitle.trim().length === 0) return
    setCreating(true)
    try {
      const chat = await rest.createGroup({
        title: groupTitle.trim(),
        memberIds: Array.from(selected),
      })
      store.actions.ingestChat(chat)
      navigate(chatPath(chat.id), { replace: true })
    } catch {
      setCreating(false)
    }
  }, [rest, store, navigate, creating, selected, groupTitle])

  const onRowClick = useCallback(
    (userId: UserId): void => {
      if (mode === 'group') toggleSelect(userId)
      else void openDirect(userId)
    },
    [mode, toggleSelect, openDirect],
  )

  const switchMode = useCallback((next: Mode): void => {
    setMode(next)
    setSelected(new Set())
    setGroupTitle('')
  }, [])

  const canCreateGroup = selected.size > 0 && groupTitle.trim().length > 0 && !creating

  const header: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    borderBottom: '1px solid var(--color-divider)',
    background: 'var(--color-surface)',
  }
  const input: CSSProperties = {
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'var(--color-bg-secondary)',
    borderRadius: 'var(--radius-pill)',
    padding: '8px var(--space-3)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-md)',
  }
  const tab = (active: boolean): CSSProperties => ({
    flex: 1,
    padding: '8px',
    border: 'none',
    cursor: 'pointer',
    background: 'transparent',
    color: active ? 'var(--color-accent)' : 'var(--color-text-muted)',
    fontWeight: active ? 'var(--weight-bold)' : 'var(--weight-regular)',
    borderBottom: active ? '2px solid var(--color-accent)' : '2px solid transparent',
    fontSize: 'var(--font-md)',
  })

  const subtitleOf = (u: PublicUser): string =>
    [u.title, u.department].filter((s) => s !== undefined && s.length > 0).join(' · ')

  const list = useMemo(() => users, [users])

  return (
    <div data-screen="new-chat" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div style={header}>
        <IconButton icon="back" label={strings.nav.back} onClick={() => navigate('/')} />
        <span style={{ flex: 1, fontSize: 'var(--font-lg)', fontWeight: 'var(--weight-bold)' }}>
          {mode === 'group' ? strings.nav.newGroup : strings.nav.newChat}
        </span>
        {mode === 'group' ? (
          <Button
            variant="primary"
            disabled={!canCreateGroup}
            data-create-group=""
            onClick={() => void createGroup()}
            style={{ opacity: canCreateGroup ? 1 : 0.5 }}
          >
            {strings.settings.save}
          </Button>
        ) : null}
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-divider)' }}>
        <button type="button" data-tab="direct" style={tab(mode === 'direct')} onClick={() => switchMode('direct')}>
          {strings.nav.newChat}
        </button>
        <button type="button" data-tab="group" style={tab(mode === 'group')} onClick={() => switchMode('group')}>
          {strings.nav.newGroup}
        </button>
      </div>

      {mode === 'group' ? (
        <div style={{ padding: 'var(--space-2) var(--space-3)', borderBottom: '1px solid var(--color-divider)' }}>
          <input
            data-group-title=""
            value={groupTitle}
            placeholder={strings.auth.name}
            onChange={(e) => setGroupTitle(e.target.value)}
            style={{ ...input, width: '100%', background: 'var(--color-bg-secondary)' }}
          />
        </div>
      ) : null}

      <div style={{ ...header, borderBottom: '1px solid var(--color-divider)', background: 'var(--color-bg)' }}>
        <input
          data-directory-search=""
          autoFocus
          value={query}
          placeholder={strings.search.placeholder}
          onChange={(e) => setQuery(e.target.value)}
          style={input}
        />
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-4)' }}>
          <Spinner />
        </div>
      ) : list.length === 0 ? (
        <EmptyState title={strings.empty.directoryEmpty} />
      ) : (
        <div data-directory-list="" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {list.map((u) => {
            const isSelected = selected.has(u.id)
            const sub = subtitleOf(u)
            return (
              <button
                key={u.id}
                type="button"
                data-directory-item={u.id}
                onClick={() => onRowClick(u.id)}
                disabled={creating && mode === 'direct'}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  background: isSelected ? 'var(--color-surface-hover)' : 'transparent',
                  borderBottom: '1px solid var(--color-divider)',
                  padding: 'var(--space-2) var(--space-3)',
                  color: 'var(--color-text)',
                  cursor: 'pointer',
                }}
              >
                <Avatar name={u.name} src={u.avatarUrl} colorKey={u.id} size={44} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontWeight: 'var(--weight-medium)' }}>{u.name}</span>
                  {sub.length > 0 ? (
                    <span style={{ display: 'block', fontSize: 'var(--font-xs)', color: 'var(--color-text-muted)' }}>
                      {sub}
                    </span>
                  ) : null}
                </span>
                {mode === 'group' && isSelected ? (
                  <IconButton icon="check" label="" size={18} tabIndex={-1} style={{ pointerEvents: 'none' }} />
                ) : null}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
